import { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { api } from "../../../env";
import Input, { useInput } from "../../../components/input";
import Loading from "../../../components/loading";
import { ChartState, getDefaultDates, toISODate } from "./charts";
import MonthDrilldown from "./MonthDrilldown";

export default function TimeseriesBarChart({
    programId,
    endpoint,
    dataKey,
    seriesName,
    drilldownConfig,
}) {
    const { today, defaultStart } = getDefaultDates();
    const startInput = useInput(
        "Start Date",
        "calendar_today",
        "date",
        [],
        toISODate(defaultStart),
    );
    const endInput = useInput(
        "End Date",
        "event",
        "date",
        [],
        toISODate(today),
    );
    const [categories, setCategories] = useState([]);
    const [seriesData, setSeriesData] = useState([]);
    const [chartState, setChartState] = useState(ChartState.LOADING);
    const [selectedMonth, setSelectedMonth] = useState(null);

    // Apex keeps the event handlers from the options it was created with,
    // so read categories through a ref to always get the current ones
    const categoriesRef = useRef(categories);
    categoriesRef.current = categories;

    useEffect(() => {
        setSelectedMonth(null);
        if (!programId) return;
        // A cleared date would leave the old chart showing, and its drilldown would query a different range
        if (!startInput._value || !endInput._value) {
            setChartState(ChartState.INCOMPLETE_RANGE);
            return;
        }
        setChartState(ChartState.LOADING);
        const controller = new AbortController();
        api.get(`programs/${programId}/timeseries/${endpoint}`, {
            signal: controller.signal,
            params: { startDate: startInput._value, endDate: endInput._value },
        })
            .then((res) => {
                const data = res.data.data;
                setCategories(data.map((d) => d.month));
                setSeriesData(data.map((d) => (d[dataKey] || 0.005) * 100));
                setChartState(
                    data.length === 0 ? ChartState.NO_DATA : ChartState.SHOWING,
                );
            })
            .catch(() => {
                // Aborted by a newer effect run, which owns the chart state now
                if (!controller.signal.aborted) setChartState(ChartState.ERROR);
            });
        return () => controller.abort();
    }, [programId, endpoint, dataKey, startInput._value, endInput._value]);

    const selectMonth = (index) => {
        const month = categoriesRef.current[index];
        if (month) setSelectedMonth(month);
    };

    const accentColor = `rgb(${getComputedStyle(document.documentElement).getPropertyValue("--accent").trim()})`;

    const options = {
        chart: {
            type: "bar",
            height: 350,
            events: drilldownConfig
                ? {
                      dataPointSelection: (
                          event,
                          chartContext,
                          { dataPointIndex },
                      ) => selectMonth(dataPointIndex),
                      // Month labels are clickable too, since 0% bars are tiny and hard to hit
                      xAxisLabelClick: (event, chartContext, { labelIndex }) =>
                          selectMonth(labelIndex),
                  }
                : {},
        },
        // Don't keep the clicked bar highlighted, the drilldown panel shows which month is selected
        states: { active: { filter: { type: "none" } } },
        colors: [accentColor],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "25%",
                borderRadius: 5,
                borderRadiusApplication: "end",
            },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: { categories },
        yaxis: {
            min: 0,
            max: 100,
            title: { text: "Rate (%)" },
            labels: { formatter: (val) => `${Math.round(val)}` },
        },
        fill: { opacity: 1 },
        tooltip: {
            y: { formatter: (val) => `${Math.round(val === 0.5 ? 0 : val)}%` },
        },
    };

    return (
        <div>
            <div className="flex gap-s v-spacer-xs">
                <Input state={startInput} />
                <Input state={endInput} />
            </div>
            <div className={drilldownConfig ? "clickable-chart" : ""}>
                {chartState === ChartState.LOADING && <Loading />}
                {chartState === ChartState.ERROR && (
                    <div className="error-card standard-padding">
                        <p className="card-title error">Error fetching data</p>
                    </div>
                )}
                {chartState === ChartState.NO_DATA && (
                    <div className="dashed-card standard-padding">
                        <p className="card-title">No Data</p>
                    </div>
                )}
                {chartState === ChartState.INCOMPLETE_RANGE && (
                    <div className="dashed-card standard-padding">
                        <p className="card-title">Select a start and end date</p>
                    </div>
                )}
                {chartState === ChartState.SHOWING && (
                    <ReactApexChart
                        options={options}
                        series={[{ name: seriesName, data: seriesData }]}
                        type="bar"
                        height={350}
                    />
                )}
            </div>
            {chartState === ChartState.SHOWING &&
                selectedMonth &&
                drilldownConfig && (
                    <MonthDrilldown
                        programId={programId}
                        endpoint={endpoint}
                        month={selectedMonth}
                        startDate={startInput._value}
                        endDate={endInput._value}
                        config={drilldownConfig}
                        onClose={() => setSelectedMonth(null)}
                    />
                )}
        </div>
    );
}